import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabase";

export default function DonorList() {
  const [donations, setDonations] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState("created_at");
  const [sortAsc, setSortAsc] = useState(false);

  const fetchData = useCallback(async () => {
    const [donRes, playerRes] = await Promise.all([
      supabase.from("donations").select("*").order("created_at", { ascending: false }),
      supabase.from("players").select("id, name"),
    ]);
    if (donRes.data) setDonations(donRes.data);
    if (playerRes.data) setPlayers(playerRes.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const playerMap = {};
  players.forEach((p) => {
    playerMap[p.id] = p.name;
  });

  const sorted = [...donations].sort((a, b) => {
    let va = a[sortField];
    let vb = b[sortField];
    if (sortField === "amount") {
      va = Number(va);
      vb = Number(vb);
    }
    if (va < vb) return sortAsc ? -1 : 1;
    if (va > vb) return sortAsc ? 1 : -1;
    return 0;
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(field === "created_at" ? false : true);
    }
  };

  const SortArrow = ({ field }) => {
    if (sortField !== field) return null;
    return <span className="ml-1">{sortAsc ? "\u2191" : "\u2193"}</span>;
  };

  const exportCSV = () => {
    const header = "Donor Name,Email,Amount,Player,Message,Date\n";
    const rows = sorted
      .map((d) => {
        const player = d.player_id ? playerMap[d.player_id] || "Unknown" : "Team";
        const date = new Date(d.created_at).toLocaleDateString();
        const msg = (d.message || "").replace(/"/g, '""');
        const name = d.is_anonymous ? "Anonymous" : d.donor_name;
        return `"${name}","${d.donor_email}",${d.amount},"${player}","${msg}","${date}"`;
      })
      .join("\n");

    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "griffins-donors.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-navy/40">Loading donors...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold text-navy">
          Donors{" "}
          <span className="text-navy/40 font-normal text-lg">
            ({donations.length})
          </span>
        </h1>
        <button
          onClick={exportCSV}
          className="border border-warm-gray text-navy text-sm font-semibold py-2.5 px-5 rounded-lg hover:bg-warm-gray transition-colors"
        >
          Export CSV
        </button>
      </div>

      <div className="bg-white rounded-xl border border-warm-gray overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-warm-gray bg-cream/50">
                <th className="text-left px-5 py-2.5 font-semibold text-navy/60">
                  Donor
                </th>
                <th className="text-left px-5 py-2.5 font-semibold text-navy/60">
                  Email
                </th>
                <th
                  className="text-right px-5 py-2.5 font-semibold text-navy/60 cursor-pointer hover:text-navy select-none"
                  onClick={() => toggleSort("amount")}
                >
                  Amount
                  <SortArrow field="amount" />
                </th>
                <th className="text-left px-5 py-2.5 font-semibold text-navy/60">
                  Player
                </th>
                <th className="text-left px-5 py-2.5 font-semibold text-navy/60 hidden sm:table-cell">
                  Message
                </th>
                <th
                  className="text-right px-5 py-2.5 font-semibold text-navy/60 cursor-pointer hover:text-navy select-none"
                  onClick={() => toggleSort("created_at")}
                >
                  Date
                  <SortArrow field="created_at" />
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((d) => (
                <tr
                  key={d.id}
                  className="border-b border-warm-gray last:border-0 hover:bg-cream/30"
                >
                  <td className="px-5 py-3 font-medium text-navy">
                    {d.is_anonymous ? "Anonymous" : d.donor_name}
                  </td>
                  <td className="px-5 py-3 text-navy/50 truncate max-w-[160px]">
                    {d.donor_email}
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-navy">
                    ${Number(d.amount)}
                  </td>
                  <td className="px-5 py-3 text-navy/60">
                    {d.player_id ? playerMap[d.player_id] || "—" : "Team"}
                  </td>
                  <td className="px-5 py-3 text-navy/50 truncate max-w-[200px] hidden sm:table-cell">
                    {d.message || "—"}
                  </td>
                  <td className="px-5 py-3 text-right text-navy/50 whitespace-nowrap">
                    {new Date(d.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
              {donations.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-navy/40">
                    No donations yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
